/**
 * 表单设计器：字段类型定义（左侧字段库）与字段工厂。
 */

export const FIELD_GROUPS = [
  {
    title: '基础字段',
    items: [
      { type: 'input', label: '单行文本', icon: 'el-icon-edit-outline' },
      { type: 'textarea', label: '多行文本', icon: 'el-icon-document' },
      { type: 'number', label: '数字', icon: 'el-icon-sort' },
      { type: 'money', label: '金额', icon: 'el-icon-coin' },
      { type: 'date', label: '日期', icon: 'el-icon-date' },
      { type: 'readonlyText', label: '只读文本', icon: 'el-icon-view' }
    ]
  },
  {
    title: '选择字段',
    items: [
      { type: 'select', label: '下拉选择', icon: 'el-icon-arrow-down' },
      { type: 'radio', label: '单选框', icon: 'el-icon-circle-check' },
      { type: 'checkbox', label: '多选框', icon: 'el-icon-finished' }
    ]
  },
  {
    title: '高级字段',
    items: [
      { type: 'user', label: '成员', icon: 'el-icon-user' },
      { type: 'dept', label: '部门', icon: 'el-icon-office-building' },
      { type: 'chance', label: '商机', icon: 'el-icon-suitcase' },
      { type: 'project', label: '项目', icon: 'el-icon-folder' },
      { type: 'upload', label: '附件', icon: 'el-icon-paperclip' },
      { type: 'detail', label: '明细/表格', icon: 'el-icon-tickets' },
      { type: 'cascade', label: '级联', icon: 'el-icon-share' },
      { type: 'curUser', label: '当前用户id', icon: 'el-icon-user-solid' },
      { type: 'userRole', label: '用户角色', icon: 'el-icon-s-check' },
      { type: 'total', label: '总价', icon: 'el-icon-money' },
      { type: 'sqlValue', label: '取值', icon: 'el-icon-search' },
      { type: 'ctrlNumber', label: '受控数字', icon: 'el-icon-lock' }
    ]
  },
  {
    title: '布局',
    items: [
      { type: 'tips', label: '说明文字', icon: 'el-icon-info' }
    ]
  }
]

const TYPE_LABEL = {
  input: '单行文本', textarea: '多行文本', number: '数字', money: '金额', date: '日期',
  select: '下拉选择', radio: '单选框', checkbox: '多选框', user: '成员', dept: '部门',
  chance: '商机', project: '项目', upload: '附件', tips: '说明文字', detail: '明细/表格', cascade: '级联',
  curUser: '当前用户id', total: '总价', sqlValue: '取值', ctrlNumber: '受控数字',
  userRole: '用户角色', readonlyText: '只读文本'
}

/** 受控数字约束操作符 */
export const CTRL_NUMBER_OPS = [
  { value: '<', label: '小于' },
  { value: '<=', label: '小于等于' },
  { value: '>', label: '大于' },
  { value: '>=', label: '大于等于' },
  { value: '=', label: '等于' }
]

export function typeLabel(type) {
  return TYPE_LABEL[type] || type
}

/** 明细/表格字段的子列可选类型 */
export const DETAIL_COL_TYPES = [
  { type: 'input', label: '单行文本' },
  { type: 'textarea', label: '多行文本' },
  { type: 'number', label: '数字' },
  { type: 'money', label: '金额' },
  { type: 'date', label: '日期' },
  { type: 'chance', label: '商机' },
  { type: 'project', label: '项目' },
  { type: 'cascade', label: '级联' },
  { type: 'sqlValue', label: '取值' },
  { type: 'ctrlNumber', label: '受控数字' }
]

/** 附件字段：可选的附件类型预设分组（value 存入字段的 attachTypes） */
export const ATTACH_TYPE_GROUPS = [
  { value: 'word', label: 'Word', exts: ['doc', 'docx'], accept: '.doc,.docx' },
  { value: 'excel', label: 'Excel', exts: ['xls', 'xlsx'], accept: '.xls,.xlsx' },
  { value: 'ppt', label: 'PPT', exts: ['ppt', 'pptx'], accept: '.ppt,.pptx' },
  { value: 'pdf', label: 'PDF', exts: ['pdf'], accept: '.pdf' },
  { value: 'image', label: '图片', exts: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'], accept: 'image/*' },
  { value: 'archive', label: '压缩包', exts: ['zip', 'rar', '7z'], accept: '.zip,.rar,.7z' },
  { value: 'text', label: '文本', exts: ['txt', 'csv'], accept: '.txt,.csv' }
]

/** 附件字段默认允许的类型（常用办公文档） */
export const DEFAULT_ATTACH_TYPES = ['word', 'excel', 'ppt', 'pdf', 'image']

/** 解析自定义扩展名文本（支持逗号/顿号/分号/空格分隔），返回小写、去点、去重的数组 */
function parseCustomExts(text) {
  const out = []
  String(text || '').split(/[,，、;；\s]+/).forEach(item => {
    // 去掉前导点与任何非字母数字字符，容错用户误输入
    const ext = item.trim().replace(/^\.+/, '').replace(/[^0-9a-zA-Z]/g, '').toLowerCase()
    if (ext && out.indexOf(ext) < 0) out.push(ext)
  })
  return out
}

/** 附件字段：允许的扩展名集合（小写、无点）；空数组表示不限制 */
export function attachAllowedExts(field) {
  const out = []
  const types = (field && field.attachTypes) || []
  types.forEach(k => {
    const g = ATTACH_TYPE_GROUPS.find(x => x.value === k)
    if (g) g.exts.forEach(e => { if (out.indexOf(e) < 0) out.push(e) })
  })
  parseCustomExts(field && field.attachExts).forEach(e => { if (out.indexOf(e) < 0) out.push(e) })
  return out
}

/** 附件字段：el-upload 的 accept 属性值；不限制时返回空串 */
export function attachAccept(field) {
  const parts = []
  const types = (field && field.attachTypes) || []
  types.forEach(k => {
    const g = ATTACH_TYPE_GROUPS.find(x => x.value === k)
    if (g && g.accept && parts.indexOf(g.accept) < 0) parts.push(g.accept)
  })
  parseCustomExts(field && field.attachExts).forEach(e => parts.push('.' + e))
  return parts.join(',')
}

/** 附件字段：允许类型的展示文案；不限制时返回空串 */
export function attachTipText(field) {
  const labels = []
  const types = (field && field.attachTypes) || []
  types.forEach(k => {
    const g = ATTACH_TYPE_GROUPS.find(x => x.value === k)
    if (g) labels.push(g.label)
  })
  const exts = parseCustomExts(field && field.attachExts).map(e => '.' + e)
  if (!labels.length && !exts.length) return ''
  return labels.concat(exts).join('、')
}

function genKey(type) {
  const t = type || 'field'
  return t + '_' + Date.now().toString(36).slice(-5) + Math.floor(Math.random() * 90 + 10)
}

/**
 * 创建明细/表格字段的一个默认子列对象。
 */
export function createColumn(type, label) {
  const t = type || 'input'
  const col = { key: genKey('col'), label: label || typeLabel(t), type: t, required: false }
  if (t === 'money') Object.assign(col, { unit: '元', showUpper: true })
  if (t === 'chance' || t === 'project') col.scope = 'all'
  if (t === 'cascade') Object.assign(col, { sql: '', levels: [] })
  if (t === 'sqlValue') Object.assign(col, { sql: '', valueCol: '' })
  if (t === 'ctrlNumber') Object.assign(col, { constraints: [] })
  return col
}

/**
 * 依据类型创建一个默认字段对象。
 */
export function createField(type) {
  const base = { type, key: genKey(type), label: typeLabel(type), required: false }
  switch (type) {
    case 'input':
    case 'textarea':
      return Object.assign(base, { placeholder: '' })
    case 'readonlyText':
      // content：设计时写入的只读文本，支持 ${字段key} 引用其他字段值
      return Object.assign(base, { content: '' })
    case 'money':
      return Object.assign(base, { unit: '元', showUpper: true })
    case 'upload':
      return Object.assign(base, { attachTypes: DEFAULT_ATTACH_TYPES.slice(), attachExts: '' })
    case 'select':
    case 'radio':
    case 'checkbox':
      return Object.assign(base, {
        options: [{ label: '选项1', value: '1' }, { label: '选项2', value: '2' }],
        multiple: type === 'checkbox'
      })
    case 'user':
      // deptFilterKey：关联的「部门」字段 key，运行时按该字段所选部门过滤成员（'' = 不限制）
      // deptIncludeChildren：是否包含所选部门的子部门成员（缺省即开启，兼容旧表单）
      return Object.assign(base, { multiple: false, deptFilterKey: '', deptIncludeChildren: true })
    case 'dept':
      // centerScope：限定的中心范围（一级组织 id 数组）；[] 或缺失 = 不限制（全部中心/顶级组织）
      return Object.assign(base, { multiple: false, centerScope: [] })
    case 'chance':
      return Object.assign(base, { scope: 'all', scopeRules: [] })
    case 'project':
      return Object.assign(base, { scope: 'all', scopeRules: [] })
    case 'tips':
      return { type, key: genKey(type), label: '说明', content: '请填写说明文字' }
    case 'detail':
      return Object.assign(base, { columns: [createColumn('input', '名称'), createColumn('number', '数量')] })
    case 'cascade':
      return Object.assign(base, { sql: '', levels: [] })
    case 'total':
      return Object.assign(base, { detailKey: '', qtyCol: '', priceCol: '', unit: '元' })
    case 'sqlValue':
      return Object.assign(base, { sql: '', valueCol: '' })
    case 'ctrlNumber':
      return Object.assign(base, { constraints: [] })
    case 'userRole':
      return Object.assign(base, { visible: true })
    default:
      return base
  }
}

/** 只读文本：解析 content 中的 ${字段key} 引用列表（去重、保持出现顺序） */
export function readonlyTextPlaceholders(content) {
  const re = /\$\{([A-Za-z][A-Za-z0-9_]*)\}/g
  const out = []
  let m
  while ((m = re.exec(String(content || '')))) {
    if (out.indexOf(m[1]) < 0) out.push(m[1])
  }
  return out
}

/** 操作符（供流程条件分支复用） */
export const CONDITION_OPS = [
  { value: '=', label: '等于' },
  { value: '!=', label: '不等于' },
  { value: '>', label: '大于' },
  { value: '>=', label: '大于等于' },
  { value: '<', label: '小于' },
  { value: '<=', label: '小于等于' },
  { value: 'contains', label: '包含' },
  { value: 'in', label: '属于' },
  { value: 'empty', label: '为空' },
  { value: 'notEmpty', label: '不为空' }
]
